/** Mock debug telemetry and device properties for the debug panel UI. */

export const DEBUG_STREAM_TABS = [
  { id: "gpscan", label: "gpscan" },
  { id: "io", label: "io" },
  { id: "coprocio", label: "coprocio" },
  { id: "cellinfo", label: "cellinfo" },
  { id: "fota", label: "fota" },
  { id: "movinfo", label: "movinfo" },
  { id: "debug", label: "debug" },
  { id: "parameters", label: "parameters" },
];

export const DEBUG_ACTIONS = {
  positive: [
    { id: "out1-en", label: "Output 1 Enable" },
    { id: "out2-en", label: "Output 2 Enable" },
    { id: "out2-en-500", label: "Output 2 Enable 500ms" },
    { id: "unlock", label: "Unlock" },
    { id: "kle-unlock", label: "KLE Unlock" },
  ],
  negative: [
    { id: "out1-dis", label: "Output 1 Disable" },
    { id: "out2-dis", label: "Output 2 Disable" },
    { id: "lock", label: "Lock" },
    { id: "kle-lock", label: "KLE Lock" },
  ],
};

function shortId(deviceId) {
  return String(deviceId ?? "").slice(0, 16).toUpperCase();
}

export function buildDeviceDebugMeta(device) {
  const type = (device?.deviceType ?? "laf").toUpperCase();
  const id = device?.deviceId ?? "—";
  return {
    title: `${type}: ${id}`,
    subtitle: `(VZE1RA1TFSUN00004 — 513022)`,
    lastConnected: "2026-05-29 13:41:03",
  };
}

export function buildDeviceDebugProperties(device) {
  const id = device?.deviceId ?? "—";
  return [
    ["Last connected time", "2026-05-29 13:41:03"],
    ["Device no", shortId(id)],
    ["IMEI", id.length >= 15 ? id.slice(0, 15) : id],
    ["SIM no", device?.simNumber ?? "—"],
    ["FW version", "2.4.1"],
    ["LAF bootloader", "1.0.8"],
    ["LAF firmware", "3.2.0"],
    ["Coprocessor firmware", "1.1.4"],
    ["LAF settings", "default-v2"],
    ["Coprocessor settings", "can-extended"],
    ["APN", "onomondo"],
    ["Host", "telemetry.example.io:443"],
    ["Network", "Orange F"],
    ["Modem model", "SIM7600"],
    ["Public key", "SHA256:9f3a…c21b"],
    ["External flash ID", "W25Q128"],
  ];
}

const GPSCAN_PRIMARY = {
  proctime: 1716990063,
  latitude: 12.9716,
  longitude: 77.5946,
  speed: 42,
  heading: 186,
  altitude: 921,
  numsatv: 9,
  carbattery: 13.8,
  ignstatus: 1,
  odometer: 128450,
};

const GPSCAN_SECONDARY = {
  candata: {
    month_code: "07 (3 hours ago)",
    gnss_fix_status: "3D fix",
    gnss_latitude: 12.971598,
    gnss_longitude: 77.594612,
    gnss_speed_kmh: 42.1,
    gnss_heading: 185.7,
    gnss_altitude_m: 921.4,
    can_engine_rpm: 1840,
    can_fuel_level_pct: 67,
  },
};

export function getDebugStreamPayload(streamId) {
  const payloads = {
    gpscan: { primary: GPSCAN_PRIMARY, secondary: GPSCAN_SECONDARY },
    io: {
      primary: { input1: 1, input2: 0, output1: 1, output2: 0, relay_state: "active" },
      secondary: { digital_in_mask: "0x03", digital_out_mask: "0x01", adc_ch1_mv: 13800 },
    },
    coprocio: {
      primary: { coproc_status: "online", uart_baud: 115200, can_bus: "active" },
      secondary: { coproc_uptime_s: 86412, coproc_errors: 0, last_handshake: "2s ago" },
    },
    cellinfo: {
      primary: { operator: "Orange F", rssi: -72, rsrp: -98, network: "LTE" },
      secondary: { iccid: "89914500000000000558", imsi: "404…", band: "B3" },
    },
    fota: {
      primary: { current_version: "3.2.0", pending_version: null, last_check: "2026-05-28" },
      secondary: { fota_status: "idle", download_progress: 0 },
    },
    movinfo: {
      primary: { motion_state: "moving", last_move_at: "2026-05-29 13:40:11" },
      secondary: { idle_duration_s: 0, trip_id: "trip-8841" },
    },
    debug: {
      primary: { log_level: "info", buffer_size: 128, entries: 3 },
      secondary: { last_error: null, watchdog_resets: 0 },
    },
    parameters: {
      primary: { report_interval_s: 30, heartbeat_s: 120, gps_mode: "high" },
      secondary: { apn: "onomondo", host: "telemetry.example.io" },
    },
  };
  return payloads[streamId] ?? payloads.gpscan;
}

export function formatJson(value) {
  return JSON.stringify(value, null, 2);
}
